(() => {
  "use strict";
  const configElement = document.getElementById("DeclockReferenceConfig");
  if (!configElement || window.DeclockReferenceSearch) return;
  const config = JSON.parse(configElement.textContent);
  const TTL = 60000;
  const PAGE_SIZE = 20;
  const key =
    "declock-refs-v5:" +
    config.themeId +
    ":" +
    config.locale +
    ":" +
    config.currency;
  let indexCache;
  let indexFlight;
  const cardsCache = new Map();
  const normalize = (value) =>
    String(value || "")
      .normalize("NFKC")
      .toUpperCase()
      .replace(/[\s.\/_-]/g, "");
  const isReference = (value) =>
    /^[a-z0-9\s.\/_-]+$/i.test(String(value).normalize("NFKC").trim()) &&
    (normalize(value).match(/\d/g) || []).length >= 3;
  function referenceValues(reference, title, skus = [], handle = "") {
    const base = normalize(reference);
    const titleCodes = String(title || "")
      .normalize("NFKC")
      .replace(/\s*([.\/_-])\s*/g, "$1")
      .match(/[a-z0-9]+(?:[.\/_-][a-z0-9]+)*/gi) || [];
    // A full title reference can extend the shorter reference metafield.
    const aliases = titleCodes.map(normalize).filter((code) =>
      (code.match(/\d/g) || []).length >= 3 && (!base || code.startsWith(base)),
    );
    // Retain a full reference in an older URL only when its base is verified.
    const urlCodes = base ? [...String(handle).matchAll(/(?=(?:^|-)([a-z]*\d[a-z0-9]*(?:-\d+[a-z0-9]*)*))/gi)]
      .map((match) => normalize(match[1])).filter((code) => code.startsWith(base)) : [];
    return [...new Set([reference, ...skus, ...aliases, ...urlCodes].map(normalize).filter(Boolean))];
  }
  const rowReferences = (row) =>
    referenceValues(row[0], row[4]?.title, row[4]?.skus || [], row[1]);
  const countLabel = (count) =>
    count === 1
      ? config.labels.one
      : config.labels.count.replace("__COUNT__", String(count));
  const current = (instance, term) =>
    instance.isConnected && instance.getQuery() === term;
  function urlFor(path, params) {
    const url = new URL(path, window.location.origin);
    for (const [name, value] of Object.entries(params))
      url.searchParams.set(name, value);
    const preview = new URL(window.location.href).searchParams.get(
      "preview_theme_id",
    );
    if (preview) url.searchParams.set("preview_theme_id", preview);
    return url;
  }
  async function readJSON(url) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, {
        signal: controller.signal,
        credentials: "same-origin",
      });
      if (!response.ok)
        throw new Error("Reference search: HTTP " + response.status);
      const data = await response.json();
      if (data.version !== 1 || !Array.isArray(data.products))
        throw new Error("Invalid reference response");
      return data;
    } finally {
      clearTimeout(timeout);
    }
  }
  async function getIndex() {
    if (indexCache && Date.now() - indexCache.time < TTL)
      return indexCache.rows;
    if (indexFlight) return indexFlight;
    try {
      const saved = JSON.parse(sessionStorage.getItem(key) || "null");
      if (
        saved &&
        saved.time <= Date.now() &&
        Date.now() - saved.time < TTL &&
        Array.isArray(saved.rows)
      ) {
        indexCache = saved;
        return saved.rows;
      }
    } catch (_) {
      /* Storage may be unavailable in private browsing. */
    }
    indexFlight = (async () => {
      const base = config.root.replace(/\/$/, "") + "/collections/all";
      const page = (number) =>
        readJSON(
          urlFor(base, {
            view: "declock-reference-index",
            page: number,
            sort_by: "title-ascending",
          }),
        );
      const first = await page(1);
      if (
        !Number.isInteger(first.pages) ||
        first.pages > 100 ||
        first.total > 25000
      )
        throw new Error("Reference index exceeds Liquid pagination");
      const withFacets = (data) =>
        data.products.map((row) =>
          Array.isArray(row) && data.facets?.[row[1]]
            ? [...row.slice(0, 4), data.facets[row[1]]]
            : row,
        );
      const all = withFacets(first);
      for (let start = 2; start <= first.pages; start += 4) {
        const numbers = Array.from(
          { length: Math.min(4, first.pages - start + 1) },
          (_, i) => start + i,
        );
        const pages = await Promise.all(numbers.map(page));
        for (let i = 0; i < pages.length; i++) {
          if (pages[i].page !== numbers[i])
            throw new Error("Invalid reference page");
          all.push(...withFacets(pages[i]));
        }
      }
      const unique = new Map();
      for (const row of all) {
        if (
          Array.isArray(row) &&
          row.length >= 4 &&
          typeof row[0] === "string" &&
          /^[a-z0-9-]+$/.test(row[1])
        )
          unique.set(row[1], row);
      }
      indexCache = { time: Date.now(), rows: [...unique.values()] };
      try {
        sessionStorage.setItem(key, JSON.stringify(indexCache));
      } catch (_) {
        /* The in-memory cache still works. */
      }
      return indexCache.rows;
    })();
    try {
      return await indexFlight;
    } finally {
      indexFlight = null;
    }
  }
  async function matches(term) {
    if (!isReference(term)) return [];
    const query = normalize(term);
    const rows = await getIndex();
    return rows
      .filter((row) => rowReferences(row).some((code) => code.startsWith(query)))
      .sort((a, b) => {
        const exact =
          Number(rowReferences(b).includes(query)) - Number(rowReferences(a).includes(query));
        return (
          exact ||
          a[3].localeCompare(b[3], config.locale) ||
          a[1].localeCompare(b[1])
        );
      });
  }
  async function cards(rows, term, comparison = false) {
    if (!rows.length) return [];
    if (rows.length > PAGE_SIZE) throw new Error("Too many reference cards");
    const handles = rows.map((row) => row[1]).join("|");
    const cacheKey = (comparison ? "model-comparison-v1:" : "") + handles;
    let cached = cardsCache.get(cacheKey);
    if (!cached || Date.now() - cached.time >= TTL) {
      const data = await readJSON(
        urlFor(config.searchUrl, {
          view: "declock-reference-cards",
          q: handles,
          ...(comparison ? { model_comparison: "1" } : {}),
        }),
      );
      cached = { time: Date.now(), products: data.products };
      cardsCache.set(cacheKey, cached);
    }
    const requested = new Set(rows.map((row) => row[1]));
    return cached.products.filter(
      (item) =>
        requested.has(item.handle) &&
        referenceValues(item.reference, item.title, item.skus || [], item.handle)
          .some((code) => code.startsWith(normalize(term))),
    );
  }
  function predictiveMarkup(products, term, total) {
    const fragment = document
      .getElementById("DeclockReferencePredictive")
      .content.cloneNode(true);
    fragment.querySelector("ul").innerHTML = products
      .map((item) => item.predictive)
      .join("");
    fragment.querySelector(
      "[data-predictive-search-search-for-text]",
    ).textContent = config.labels.searchFor.replace("__TERM__", term);
    const status = fragment.querySelector(
      "[data-predictive-search-live-region-count-value]",
    );
    status.textContent = countLabel(total);
    status.dataset.totalResults = String(total);
    return fragment.firstElementChild.outerHTML;
  }
  function installPredictive() {
    customElements.whenDefined("predictive-search").then(() => {
      const prototype = customElements.get("predictive-search").prototype;
      const nativeSearch = prototype.getSearchResults;
      if (typeof nativeSearch !== "function") return;
      prototype.getSearchResults = function (term) {
        const requestId = (this.declockReferenceRequest =
          (this.declockReferenceRequest || 0) + 1);
        if (!isReference(term)) return nativeSearch.call(this, term);
        this.setLiveRegionLoadingState();
        const stillCurrent = () =>
          current(this, term) && this.declockReferenceRequest === requestId;
        matches(term)
          .then(async (rows) => {
            if (!stillCurrent()) return;
            if (!rows.length) return nativeSearch.call(this, term);
            const products = await cards(
              rows.slice(0, 6),
              term,
              this.hasAttribute("data-model-comparison-search"),
            );
            if (!stillCurrent()) return;
            if (!products.length) return nativeSearch.call(this, term);
            if (!this.contains(document.activeElement)) {
              this.removeAttribute("loading");
              return;
            }
            this.renderSearchResults(
              predictiveMarkup(products, term, rows.length),
            );
            const deferred = this.dispatchSearchUpdateEvent?.(term);
            deferred?.resolve({ totalCount: rows.length });
          })
          .catch((error) => {
            console.warn("Reference search fallback:", error.message);
            if (stillCurrent()) nativeSearch.call(this, term);
          });
      };
    });
  }
  const facetNames = ["brand", "model", "reference", "factory", "quality"];
  const toolbarText = config.locale.startsWith("el")
    ? {
        filters: "Φίλτρα",
        items: "Προϊόντα",
        sort: "Ταξινόμηση:",
        brand: "Μάρκα",
        model: "Μοντέλο",
        reference: "Reference",
        factory: "Factory",
        quality: "Ποιότητα",
        price: "Τιμή",
        from: "Από",
        to: "Έως",
        apply: "Εφαρμογή",
        clear: "Καθαρισμός",
        close: "Κλείσιμο",
        empty: "Δεν βρέθηκαν προϊόντα.",
      }
    : {
        filters: "Filters",
        items: "Items",
        sort: "Sort:",
        brand: "Brand",
        model: "Model",
        reference: "Reference",
        factory: "Factory",
        quality: "Quality",
        price: "Price",
        from: "From",
        to: "To",
        apply: "Apply",
        clear: "Clear",
        close: "Close",
        empty: "No products found.",
      };
  function facetValues(row, name) {
    const value = name === "reference" ? row[0] : row[4]?.[name];
    return (Array.isArray(value) ? value : [value])
      .filter((item) => typeof item === "string" && item.trim())
      .map((item) => item.trim());
  }
  function priceBound(params, name) {
    const value = params.get(name);
    if (!value || !/^\d+(?:\.\d{1,2})?$/.test(value)) return null;
    return Math.round(Number(value) * 100);
  }
  function filterRows(rows, params) {
    const min = priceBound(params, "ref_min");
    const max = priceBound(params, "ref_max");
    return rows.filter(
      (row) =>
        (min === null || row[2] >= min) &&
        (max === null || row[2] <= max) &&
        facetNames.every((name) => {
          const selected = params.getAll("ref_" + name);
          return (
            !selected.length ||
            selected.some((value) => facetValues(row, name).includes(value))
          );
        }),
    );
  }
  function filterOptions(rows, name) {
    const counts = new Map();
    for (const row of rows)
      for (const value of new Set(facetValues(row, name)))
        counts.set(value, (counts.get(value) || 0) + 1);
    return [...counts].sort((a, b) => a[0].localeCompare(b[0], config.locale));
  }
  function installToolbar(section, allRows, rows, url) {
    const toolbar = section.querySelector(".declock-reference-toolbar");
    const button = toolbar.querySelector("[data-ref-filters]");
    button.querySelector("[data-ref-label]").textContent = toolbarText.filters;
    toolbar.querySelector("[data-ref-count]").textContent = String(rows.length);
    toolbar.querySelector("[data-ref-items]").textContent = toolbarText.items;
    const dialog = section.querySelector("dialog");
    dialog.querySelector("h2").textContent = toolbarText.filters;
    const close = dialog.querySelector("[data-ref-close]");
    close.setAttribute("aria-label", toolbarText.close);
    button.addEventListener("click", () => {
      dialog.showModal();
      button.setAttribute("aria-expanded", "true");
    });
    close.addEventListener("click", () => dialog.close());
    dialog.addEventListener("close", () => {
      button.setAttribute("aria-expanded", "false");
      button.focus();
    });
    dialog.addEventListener("click", (event) => {
      if (event.target !== dialog) return;
      const box = dialog.getBoundingClientRect();
      if (
        event.clientX < box.left ||
        event.clientX > box.right ||
        event.clientY < box.top ||
        event.clientY > box.bottom
      )
        dialog.close();
    });
    const form = dialog.querySelector("form");
    const fields = form.querySelector("[data-ref-fields]");
    for (const name of facetNames) {
      const options = filterOptions(allRows, name);
      if (!options.length) continue;
      const fieldset = document.createElement("fieldset");
      const legend = document.createElement("legend");
      legend.textContent = toolbarText[name];
      fieldset.append(legend);
      for (const [value, count] of options) {
        const label = document.createElement("label");
        const input = document.createElement("input");
        input.type = "checkbox";
        input.name = "ref_" + name;
        input.value = value;
        input.checked = url.searchParams.getAll(input.name).includes(value);
        label.append(
          input,
          document.createTextNode(value + " (" + count + ")"),
        );
        fieldset.append(label);
      }
      fields.append(fieldset);
    }
    const price = document.createElement("fieldset");
    const legend = document.createElement("legend");
    legend.textContent = toolbarText.price + " (" + config.currency + ")";
    price.append(legend);
    for (const [name, labelText] of [
      ["ref_min", toolbarText.from],
      ["ref_max", toolbarText.to],
    ]) {
      const label = document.createElement("label");
      const input = document.createElement("input");
      input.type = "number";
      input.name = name;
      input.min = "0";
      input.step = "0.01";
      input.value =
        priceBound(url.searchParams, name) === null
          ? ""
          : url.searchParams.get(name);
      label.append(document.createTextNode(labelText), input);
      price.append(label);
    }
    fields.append(price);
    form.querySelector('[type="submit"]').textContent = toolbarText.apply;
    const reset = form.querySelector("[data-ref-clear]");
    reset.textContent = toolbarText.clear;
    const targetURL = (data) => {
      const target = pageLink(1);
      for (const name of [
        ...facetNames.map((name) => "ref_" + name),
        "ref_min",
        "ref_max",
      ])
        target.searchParams.delete(name);
      for (const [name, value] of data)
        if (value !== "") target.searchParams.append(name, value);
      return target;
    };
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const min = form.elements.ref_min,
        max = form.elements.ref_max;
      max.setCustomValidity(
        min.value && max.value && Number(min.value) > Number(max.value)
          ? toolbarText.from + " > " + toolbarText.to
          : "",
      );
      if (form.reportValidity())
        window.location.assign(targetURL(new FormData(form)).href);
    });
    form.addEventListener("input", () =>
      form.elements.ref_max.setCustomValidity(""),
    );
    reset.addEventListener("click", () =>
      window.location.assign(targetURL([]).href),
    );
  }
  function pageLink(number) {
    const url = new URL(window.location.href);
    url.searchParams.set("ref_page", String(number));
    url.searchParams.delete("page");
    return url;
  }
  async function renderPage() {
    const url = new URL(window.location.href);
    if (url.pathname.replace(/\/$/, "") !== config.searchUrl.replace(/\/$/, ""))
      return;
    const term = url.searchParams.get("q") || "";
    if (!isReference(term)) return;
    if ([...url.searchParams.keys()].some((name) => name.startsWith("filter.")))
      return;
    const root = document.querySelector(".template-search");
    const header = root?.querySelector(".template-search__header");
    if (!root || !header) return;
    const loading = document.createElement("p");
    loading.className = "declock-reference-loading page-width";
    loading.setAttribute("role", "status");
    loading.textContent = config.locale.startsWith("el")
      ? "Φόρτωση..."
      : "Loading...";
    const previousBusy = root.getAttribute("aria-busy");
    root.setAttribute("aria-busy", "true");
    root.setAttribute("data-reference-pending", "");
    root.append(loading);
    try {
      const allRows = await matches(term);
      if (!allRows.length) return;
      const rows = filterRows(allRows, url.searchParams);
      const sort =
        url.searchParams.get("ref_sort") ||
        url.searchParams.get("sort_by") ||
        "relevance";
      if (sort === "price-ascending") rows.sort((a, b) => a[2] - b[2]);
      if (sort === "price-descending") rows.sort((a, b) => b[2] - a[2]);
      if (sort === "title-ascending")
        rows.sort((a, b) => a[3].localeCompare(b[3], config.locale));
      if (sort === "title-descending")
        rows.sort((a, b) => b[3].localeCompare(a[3], config.locale));
      const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
      const page = Math.min(
        pages,
        Math.max(1, parseInt(url.searchParams.get("ref_page"), 10) || 1),
      );
      const products = await cards(
        rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        term,
      );
      if (rows.length && !products.length) return;
      const oldGrid = root.querySelector("ul.product-grid");
      const section = document.createElement("div");
      section.className =
        "declock-reference-results template-search__results page-width";
      section.append(
        document
          .getElementById("DeclockReferenceToolbar")
          .content.cloneNode(true),
      );
      installToolbar(section, allRows, rows, url);
      const grid = section.querySelector("ul");
      grid.className =
        oldGrid?.className ||
        "grid product-grid grid--2-col-tablet-down grid--4-col-desktop";
      grid.innerHTML = products.map((item) => item.card).join("");
      if (!rows.length) {
        const empty = document.createElement("p");
        empty.textContent = toolbarText.empty;
        grid.after(empty);
      }
      const allowedSort = new Set([
        "relevance",
        "price-ascending",
        "price-descending",
        "title-ascending",
        "title-descending",
      ]);
      const sortOptions = (config.sortOptions || []).filter((option) =>
        allowedSort.has(option.value),
      );
      if (sortOptions.length) {
        const label = document.createElement("label");
        label.className = "declock-reference-sort";
        label.append(document.createTextNode(toolbarText.sort));
        const select = document.createElement("select");
        select.name = "ref_sort";
        for (const option of sortOptions)
          select.add(
            new Option(option.name, option.value, false, option.value === sort),
          );
        select.addEventListener("change", () => {
          const target = pageLink(1);
          target.searchParams.set("ref_sort", select.value);
          target.searchParams.delete("sort_by");
          window.location.assign(target.href);
        });
        label.append(select);
        label.append(
          document
            .getElementById("DeclockReferenceCaret")
            .content.cloneNode(true),
        );
        section.querySelector(".declock-reference-toolbar").append(label);
      }
      const pagination = section.querySelector("nav");
      pagination.setAttribute("aria-label", config.labels.products);
      if (pages > 1) {
        const addLink = (number, relation, label) => {
          const link = document.createElement("a");
          link.href = pageLink(number).href;
          link.rel = relation;
          link.title = label;
          link.setAttribute("aria-label", label);
          link.append(
            document
              .getElementById("DeclockReferenceArrow")
              .content.cloneNode(true),
          );
          pagination.append(link);
        };
        if (page > 1) addLink(page - 1, "prev", config.labels.previous);
        const position = document.createElement("span");
        position.textContent = page + " / " + pages;
        pagination.append(position);
        if (page < pages) addLink(page + 1, "next", config.labels.next);
      } else pagination.hidden = true;
      for (const child of [...root.children]) {
        if (
          child !== header &&
          child !== loading &&
          !["STYLE", "SCRIPT"].includes(child.tagName)
        )
          child.remove();
      }
      for (const empty of header.querySelectorAll(
        ".declock-search__empty-state, .declock-search__status",
      ))
        empty.setAttribute("data-reference-hidden", "");
      root.classList.remove("template-search--empty");
      root.append(section);
      document.title = countLabel(rows.length) + " | " + term + " | DECLOCK";
    } finally {
      loading.remove();
      root.removeAttribute("data-reference-pending");
      if (previousBusy === null) root.removeAttribute("aria-busy");
      else root.setAttribute("aria-busy", previousBusy);
    }
  }
  window.DeclockReferenceSearch = {
    normalize,
    isReference,
    matches,
    cards,
    facetValues,
    filterRows,
    filterOptions,
  };
  installPredictive();
  const startPage = () =>
    renderPage().catch((error) =>
      console.warn("Reference results fallback:", error.message),
    );
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", startPage, { once: true });
  else startPage();
})();
